import React, { useEffect, useState } from 'react';
import { Pressable, Skeleton, VStack, HStack } from 'native-base';
import { StoreLocations } from './StoreLocations';
import {
    FulfillmentOptionsCardProp,
    MenuType,
    Retailer,
    useCreateCheckoutMutation,
    useDutchieStore,
    useRetailersQuery,
    defaultCategories,
    OrderType,
    Hours,
    Maybe,
    useI18n,
    useSpringBigStore,
    useTheme,
} from '@springbig/shared';
import { SBDropdown } from '~/shared/SBDropdown';
import { Caption, H3 } from '~/shared/Typography';
import { useGeolocated } from 'react-geolocated';
import { FullfillmentOptions } from './FullfillmentOptions';
import { isBefore, isAfter, format, parse } from 'date-fns';
import { Modal, Button } from '~/shared/Theme';

export type StoreModalProp = {
    showStore: boolean;
    setShowStore: () => void;
};

export const storeSchedule = (orderType: OrderType, retailer: Retailer): boolean => {

    let fullfillmentOptionHours : Maybe<Hours> | undefined;
    if (orderType) {
        switch (orderType) {
        case OrderType.CurbsidePickup:
            fullfillmentOptionHours = retailer?.hours.curbsidePickup;
            break;
        case OrderType.Delivery:
            fullfillmentOptionHours = retailer?.hours.delivery;
            break;
        case OrderType.DriveThruPickup:
            fullfillmentOptionHours = retailer?.hours.driveThruPickup;
            break;
        case OrderType.Pickup:
            fullfillmentOptionHours = retailer?.hours.pickup;
            break;
        }
    }

    if (fullfillmentOptionHours) {

        const currentDayOfTheWeek = format(new Date(), 'EEEE');
        const todayHours = fullfillmentOptionHours[currentDayOfTheWeek as keyof typeof fullfillmentOptionHours];
        if (todayHours) {
            const startHour = parse(todayHours.start, 'h:mm a', new Date());
            const endHour = parse(todayHours.end, 'h:mm a', new Date());

            return todayHours.active && isAfter(new Date(), startHour) && isBefore(new Date(), endHour);
        }
    }

    return true;
};

export const StoreModal = ({ showStore, setShowStore }: StoreModalProp) => {

    const {
        highlight_text,
        text
    } = useTheme();

    const {
        retailer,
        setRetailer,
        fullfillmentOption,
        menuType,
        setMenuType,
        setMenuCategories,
        setFullfillmentOption,
        setCheckout,
        setShowStoreBottomSheet
    } = useDutchieStore();

    const { mutate: getRetailers, data: retailersData, isLoading } = useRetailersQuery();
    const [ isMerchantGeoLocation, setIsMerchantGeoLocation ] = useState(false);

    const [internalRetailer, setInternalRetailer] = useState<Retailer | undefined>();
    const [fullfillmentOptions, setFullfillmentOptions] = useState<Array<FulfillmentOptionsCardProp>>([]);
    const [internalMenuType, setInternalMenuType] = useState<MenuType | undefined>();
    const [internalFullfillmentOption, setInternalFullfillmentOption] = useState<FulfillmentOptionsCardProp | undefined>();
    const [showComfirmationModal, setShowConfirmationModal] = useState(false);
    const [selectedDistance, setSelectedDistance] = useState<number>(30);
    const distances = [30, 50, 100];
    const { mutate: createCheckout } = useCreateCheckoutMutation();
    const { t } = useI18n();
    const { merchant } = useSpringBigStore();

    const { coords, isGeolocationEnabled } =
        useGeolocated({
            positionOptions: {
                enableHighAccuracy: true,
            },
        });

    useEffect(() => {

        if (isGeolocationEnabled && coords && merchant) {
            const variables = {
                'latitude': process.env.NODE_ENV === 'development' ? 43.0459 : coords.latitude,
                'longitude': process.env.NODE_ENV === 'development' ? -81.25999999999 : coords.longitude,
                'maxDistance': selectedDistance,
                'unit': 'MI',
            };
            getRetailers(variables, { onSettled: (data) => {
                if (data?.retailersNearLocation?.length === 0 && merchant.latitude && merchant.longitude) {
                    const variables = {
                        'latitude': parseFloat(merchant.latitude),
                        'longitude': parseFloat(merchant.longitude),
                        'maxDistance': selectedDistance,
                        'unit': 'MI',
                    };
                    setIsMerchantGeoLocation(true);
                    getRetailers(variables);
                }
            } });
        }
    }, [isGeolocationEnabled, coords, getRetailers, selectedDistance, merchant]);


    useEffect(() => {

        const availableFulfillmentOptions: Array<FulfillmentOptionsCardProp> = [];
        if (retailer?.fulfillmentOptions.pickup) {
            availableFulfillmentOptions.push({ key: OrderType.Pickup, label: 'pickup', isOpen: storeSchedule(OrderType.Pickup, retailer) });
        }

        if (retailer?.fulfillmentOptions.curbsidePickup) {
            availableFulfillmentOptions.push({ key: OrderType.CurbsidePickup, label: 'curbside pickup', isOpen: storeSchedule(OrderType.CurbsidePickup, retailer) });
        }

        if (retailer?.fulfillmentOptions.driveThruPickup) {
            availableFulfillmentOptions.push({ key: OrderType.DriveThruPickup, label: 'drive thru pickup', isOpen: storeSchedule(OrderType.DriveThruPickup, retailer) });
        }

        if (retailer?.fulfillmentOptions.delivery) {
            availableFulfillmentOptions.push({ key: OrderType.Delivery, label: 'delivery', isOpen: storeSchedule(OrderType.Delivery, retailer) });
        }

        if (fullfillmentOption) {
            setInternalFullfillmentOption(fullfillmentOption);
        }
        else if (availableFulfillmentOptions && availableFulfillmentOptions.find((e) => e.isOpen)) {
            setInternalFullfillmentOption(availableFulfillmentOptions.find((e) => e.isOpen));
        }

        if (retailer) {
            setInternalRetailer(retailer);
        }

        if (menuType) {
            setInternalMenuType(menuType);
        }

        setFullfillmentOptions(availableFulfillmentOptions);
    }, [retailer]);

    const applyOptions = () => {

        if (internalRetailer === retailer) {

            if (internalMenuType) {
                setMenuType(internalMenuType);
            }

            if (internalFullfillmentOption) {
                setFullfillmentOption(internalFullfillmentOption);
            }

            setShowStore();
        }
        else {
            if (showComfirmationModal) {
                if (internalRetailer) {
                    setRetailer(internalRetailer);
                    createCheckout({
                        pricingType: internalMenuType,
                        orderType: internalFullfillmentOption?.key,
                        retailerId: internalRetailer?.id,
                    }, {
                        onSuccess(data) {

                            if (data) {
                                setCheckout(data.createCheckout);
                                setShowStore();
                                setMenuCategories(defaultCategories);
                            }
                        },
                        onError(error) {

                            console.log(error.data.errors[0].message);
                        }
                    });
                }

                if (internalMenuType) {
                    setMenuType(internalMenuType);
                }

                if (internalFullfillmentOption) {
                    setFullfillmentOption(internalFullfillmentOption);
                }
            }
            else {
                setShowConfirmationModal(true);
            }
        }
    };

    return (
        <Modal
            isOpen={showStore}
            onClose={() => setShowStore()}
            closeable
        >
            <Modal.Header>
                <H3
                    color={highlight_text}
                    text={
                        showComfirmationModal ? 'shop.alert' : 'shop.location_and_method'
                    }
                />
            </Modal.Header>

            <Modal.Body>
                { showComfirmationModal ? (
                    <VStack alignItems={'center'} space={4}>
                        <Caption
                            text={'shop.due_to_differences'}
                            textAlign={'center'}
                        />
                        <Caption
                            text={'shop.would_you_like_to_continue'}
                            textAlign={'center'}
                        />
                    </VStack>
                ) : (
                    <VStack space={2}>
                        <SBDropdown
                            value={internalFullfillmentOption?.label}
                        >
                            <FullfillmentOptions
                                fullfillmentOptions={fullfillmentOptions}
                                setFullfillmentOption={setInternalFullfillmentOption}
                                selectedFulfillmentOption={internalFullfillmentOption}
                            />
                        </SBDropdown>
                        <Caption text={'shop.at'} alignSelf={'center'} />
                        { isLoading || !isGeolocationEnabled || !coords ?
                            <Skeleton />
                            :
                            <SBDropdown value={internalRetailer?.name ?? retailer?.name}>
                                <VStack space={4}>
                                    <VStack alignItems={'center'} space={2}>
                                        <H3 text={'shop.distance'} m={0} />
                                        <HStack space={4}>
                                            { distances.map((distance, index) =>


                                                <Button
                                                    key={index}
                                                    modifier={'small'}
                                                    text={retailersData?.retailersNearLocation[0]?.locationDetail?.unit === 'MI' ? `${distance} miles` : `${distance} km`}
                                                    variant={'text'}
                                                    color={selectedDistance === distance ? highlight_text : text}
                                                    onPress={() => setSelectedDistance(distance)}
                                                />
                                            )}
                                        </HStack>
                                    </VStack>
                                    {
                                        retailersData && retailersData.retailersNearLocation.length !== 0 ?
                                            <StoreLocations
                                                setMenuType={setInternalMenuType}
                                                setRetailer={setInternalRetailer}
                                                type={'modal'}
                                                stores={isMerchantGeoLocation ?
                                                        retailersData?.retailersNearLocation?.sort((a,b) => a?.retailer?.name.localeCompare(b?.retailer?.name)) :
                                                        retailersData?.retailersNearLocation
                                                }
                                                setFullfillmentOption={setInternalFullfillmentOption}
                                                internalRetailer={internalRetailer}
                                                isMerchantGeoLocation={isMerchantGeoLocation}
                                            />
                                            :
                                            <Caption textAlign={'center'}>
                                                Shopping is Not Available in your current location
                                            </Caption>
                                    }
                                </VStack>
                            </SBDropdown>
                        }
                        { fullfillmentOptions.find((e) => !e.isOpen) ?
                            <Caption fontSize={12}>
                                <Caption text={'shop.some_fulfillment_options'} />
                                <Pressable onPress={() => {

                                    setShowStoreBottomSheet(true);
                                    setShowStore();
                                }}>
                                    <Caption
                                        ml={1}
                                        text={'shop.see_fulfillment_hours'}
                                        color={highlight_text}
                                    />
                                </Pressable>
                            </Caption>
                            :
                            <></>
                        }
                    </VStack>
                )}
            </Modal.Body>
            <Modal.Footer>
                <HStack space={4}>
                    { showComfirmationModal ?
                        <Button
                            variant={'neutral'}
                            text={'general.back'}
                            onPress={() => setShowConfirmationModal(false)}
                        />
                        : <></>
                    }
                    <Button
                        text={'shop.apply'}
                        onPress={() => applyOptions()}
                    />
                </HStack>
            </Modal.Footer>
        </Modal>
    );
};
